import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';
import {Edge} from 'reactflow';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigDescription} from 'lookup-config/LookupConfigDescription';
import {LookupConfigSpec, SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import {
  TaskSchemaNode,
  TaskSchemaNodeRowData,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import APIUtil from 'util/APIUtil';

import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {runLookupConfigModalForProcessSchemaMapper} from '../SchemaFieldLookup';
import {buildDefaultSchemaFieldMapperConfigFromDataFlowEdge} from './buildSchemaMapperConfigFromTransitionData';
import {getFirstSchemaFieldIdFromLookupConfig} from './buildTransitionDataFromTaskDefinitions';
import {
  getSchemaFieldIdFromRowIdentifier,
  getSchemaFieldInfoFromHandle,
  getTaskSchemaIdFromNodeIdentifier,
  doesDataFlowEdgeHandleReferenceRow,
} from './flowchartIdentifierUtils';
import isLookupConfigCustomMapping from './isLookupConfigCustomMapping';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  TransitionSource,
} from './transitionModalWithFlowchartEditorConstants';

export interface TransitionFieldInfoSectionProps {
  selectedFieldId: string;
  node: TaskSchemaNode;
  flowchart: {
    nodes: Array<TaskSchemaNode>;
    edges: Array<Edge>;
  };
  flowchartTaskSchema: ProcessTaskSchemaGraphQL;
  allSchemasById: SchemaById;
  transitionSource: TransitionSource | null;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  onChangeCustomMappingData: (edgeId: string, newCustomMappingData: CustomMappingData) => void;
}

function FieldDisplay(props: {
  field: {fieldIcon: BenchlingIconName; fieldName: string | JSX.Element};
}): JSX.Element {
  return (
    <div className={css(styles.fieldDisplay)}>
      <BenchlingIcon icon={props.field.fieldIcon} />
      {props.field.fieldName}
    </div>
  );
}

type MappingsTableProps = TransitionFieldInfoSectionProps & {
  transitionSource: TransitionSource;
  edges: Array<Edge>;
};

function getMappingSourceSchema(
  edge: Edge,
  allSchemasById: SchemaById,
  transitionSource: TransitionSource
): Schema | null {
  const fromSchemaId = getTaskSchemaIdFromNodeIdentifier(edge.source);
  const fromTaskSchema = allSchemasById[fromSchemaId]! as ProcessTaskSchemaGraphQL;
  const fromSchema =
    transitionSource === TransitionSource.OUTPUTS ? fromTaskSchema.outputSchema : fromTaskSchema;
  if (fromSchema == null) {
    return null;
  }
  return fromSchema;
}

function EditMappingButton(props: {
  fromSchema: Schema;
  lookupConfig: LookupConfigSpec;
  flowchartTaskSchema: ProcessTaskSchemaGraphQL;
  onChangeLookupConfig: (lookupConfig: LookupConfigSpec | null) => void;
  allSchemasById: SchemaById;
  toSchemaField: SchemaField;
  fromSchemaFieldId: string;
  fromSchemaFieldSectionName: string;
  isCustomLookupConfig: boolean;
}): JSX.Element {
  const getAdditionalLookupConfigValidationMessage = (lookupConfig: LookupConfigSpec): string | null => {
    const schemaFieldId = getFirstSchemaFieldIdFromLookupConfig(lookupConfig);
    const fromSchemaField = props.fromSchema.allFields.find(
      (field: SchemaField) => field.apiId === props.fromSchemaFieldId
    )!;
    const errorMessage = `The lookup must start with the ${fromSchemaField.name} field in the '${props.fromSchemaFieldSectionName}' section.`;
    if (schemaFieldId == null) {
      return errorMessage;
    } else if (schemaFieldId !== props.fromSchemaFieldId) {
      return errorMessage;
    }
    return null;
  };

  return (
    <Button
      buttonStyle={ButtonStyle.LINK}
      onClick={() => {
        runLookupConfigModalForProcessSchemaMapper({
          fromSchema: props.fromSchema,
          lookupConfig: props.lookupConfig,
          flowchartTaskSchema: props.flowchartTaskSchema,
          onSubmit: (lookupConfig) => {
            const newLookupConfig = isLookupConfigCustomMapping(lookupConfig, props.fromSchemaFieldId)
              ? lookupConfig
              : null;
            props.onChangeLookupConfig(newLookupConfig);
          },
          allSchemasById: props.allSchemasById,
          toSchemaField: props.toSchemaField,
          getAdditionalLookupConfigValidationMessage,
        });
      }}
      text={props.isCustomLookupConfig ? 'Edit' : 'Configure'}
      dataTestAttrs={{
        component: 'TransitionMappingsTable',
        element: props.isCustomLookupConfig ? 'editMappingButton' : 'configureMappingButton',
      }}
    />
  );
}

function EditMappingLookupCell(props: {
  targetField: TaskSchemaNodeRowData;
  dataFlowEdge: FlowchartStaticEdgeData;
  allSchemasById: SchemaById;
  flowchartTaskSchema: ProcessTaskSchemaGraphQL;
  transitionSource: TransitionSource;
  isCustomLookupConfig: boolean;
  lookupConfig: LookupConfigSpec;
  onChangeLookupConfig: (lookupConfig: LookupConfigSpec | null) => void;
}): JSX.Element | null {
  assert(props.dataFlowEdge.type === ProcessFlowchartEdgeType.DATA_FLOW, 'Expected a data flow edge');
  const targetFieldId = getSchemaFieldIdFromRowIdentifier(props.targetField.id);
  const fromSchemaId = getTaskSchemaIdFromNodeIdentifier(props.dataFlowEdge.source);
  const {fieldId: fromSchemaFieldId, taskSchemaNodeSection} = getSchemaFieldInfoFromHandle(
    props.dataFlowEdge.sourceHandle
  );

  const fromTaskSchema = props.allSchemasById[fromSchemaId]! as ProcessTaskSchemaGraphQL;
  const fromSchema =
    props.transitionSource === TransitionSource.OUTPUTS ? fromTaskSchema.outputSchema : fromTaskSchema;

  if (fromSchema == null) {
    return null;
  }

  const toSchemaId = getTaskSchemaIdFromNodeIdentifier(props.dataFlowEdge.target);
  const toTaskSchema = props.allSchemasById[toSchemaId]! as ProcessTaskSchemaGraphQL;
  const toSchema =
    toTaskSchema.executionType === ProcessExecutionType.FLOWCHART ? toTaskSchema.outputSchema : toTaskSchema;
  assert(toSchema != null, 'toSchema should not be null');

  const toSchemaField = toSchema.allFields.find((field: SchemaField) => field.apiId === targetFieldId);
  assert(toSchemaField != null, 'toSchemaField should not be null');

  return (
    <Cell>
      <div className={css(styles.editMappingButtonContainer)}>
        <EditMappingButton
          fromSchema={fromSchema}
          lookupConfig={props.lookupConfig}
          flowchartTaskSchema={props.flowchartTaskSchema}
          onChangeLookupConfig={props.onChangeLookupConfig}
          allSchemasById={props.allSchemasById}
          toSchemaField={toSchemaField}
          fromSchemaFieldId={fromSchemaFieldId}
          fromSchemaFieldSectionName={taskSchemaNodeSection}
          isCustomLookupConfig={props.isCustomLookupConfig}
        />
        {props.isCustomLookupConfig && (
          <>
            <span>|</span>
            <Button
              buttonStyle={ButtonStyle.LINK}
              onClick={() => {
                props.onChangeLookupConfig(null);
              }}
              text="Reset"
              dataTestAttrs={{
                component: 'TransitionMappingsTable',
                element: 'resetMappingButton',
              }}
            />
          </>
        )}
      </div>
    </Cell>
  );
}

export function TransitionMappingsTable(props: MappingsTableProps): JSX.Element {
  const getTargetField = (rowIndex: number): TaskSchemaNodeRowData | null => {
    const edge = props.edges[rowIndex];
    const targetNode = props.flowchart.nodes.find((node) => node.id === edge.target);

    for (const section of targetNode?.data.sections ?? []) {
      for (const row of section.sectionRows) {
        if (doesDataFlowEdgeHandleReferenceRow(edge.targetHandle!, row.id)) {
          return row;
        }
      }
    }
    return null;
  };

  const allTaskSchemasById = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(props.allSchemasById)
          .filter(([schemaId]) => APIUtil.isProcessTaskSchema(schemaId))
          .map(([schemaId, schema]) => [schemaId, schema as ProcessTaskSchemaGraphQL])
      ),
    [props.allSchemasById]
  );

  const doesAnyFieldHaveConditionalMapping = props.edges.some((edge) => {
    const customMappingData = props.customMappingDataByEdgeId[edge.id];
    return customMappingData?.resultMustBeNonNullToMap ?? false;
  });

  const getLookupConfigForEdge = (edge: FlowchartStaticEdgeData): LookupConfigSpec =>
    props.customMappingDataByEdgeId[edge.id]?.lookupConfig ??
    buildDefaultSchemaFieldMapperConfigFromDataFlowEdge({
      dataFlowEdge: edge,
      destinationNode: props.flowchart.nodes.find((node) => node.id === edge.target)!,
      transitionSource: props.transitionSource,
      allTaskSchemasById,
    }).lookupConfig;

  return (
    <Table numRows={props.edges.length}>
      <Column
        header="Source field"
        renderCell={(rowIndex) => {
          const fromSchema = getMappingSourceSchema(
            props.edges[rowIndex],
            props.allSchemasById,
            props.transitionSource
          );
          if (fromSchema == null) {
            return <Cell />;
          }
          const edge = props.edges[rowIndex] as FlowchartStaticEdgeData;
          return (
            <Cell>
              <LookupConfigDescription
                baseSchema={fromSchema}
                lookupConfig={getLookupConfigForEdge(edge)}
                context={{flowchartTaskSchema: props.flowchartTaskSchema}}
              />
            </Cell>
          );
        }}
      />
      <Column
        header="Source field lookup"
        renderCell={(rowIndex) => {
          const targetField = getTargetField(rowIndex);
          if (targetField == null) {
            return null;
          }
          const edge = props.edges[rowIndex] as FlowchartStaticEdgeData;
          return (
            <EditMappingLookupCell
              targetField={targetField}
              dataFlowEdge={edge}
              allSchemasById={props.allSchemasById}
              flowchartTaskSchema={props.flowchartTaskSchema}
              transitionSource={props.transitionSource}
              lookupConfig={getLookupConfigForEdge(edge)}
              onChangeLookupConfig={(lookupConfig) => {
                props.onChangeCustomMappingData(props.edges[rowIndex].id, {
                  ...(props.customMappingDataByEdgeId[props.edges[rowIndex].id] ?? {}),
                  lookupConfig: lookupConfig ?? undefined,
                });
              }}
              isCustomLookupConfig={
                props.customMappingDataByEdgeId[props.edges[rowIndex].id]?.lookupConfig != null
              }
            />
          );
        }}
      />
      <Column
        header="Target field"
        renderCell={(rowIndex) => {
          const targetField = getTargetField(rowIndex);
          if (targetField == null) {
            return <Cell />;
          }
          return (
            <Cell>
              <FieldDisplay
                field={{
                  fieldIcon: targetField.fieldIcon,
                  fieldName: targetField.fieldName,
                }}
              />
            </Cell>
          );
        }}
      />
      {(BenchlingConfig.WORKFLOWS_ENABLE_CONDITIONAL_MAPPING || doesAnyFieldHaveConditionalMapping) && (
        <Column
          header={
            <Cell>
              <span>
                <span>Required</span>
                <Tooltip title="Check the box to prevent items with empty or null values for the specified field from being created.">
                  <BenchlingIcon icon="info-circle" className={css(styles.helpIcon)} />
                </Tooltip>
              </span>
            </Cell>
          }
          renderCell={(rowIndex) => {
            const inputId = _.uniqueId('resultMustBeNonNullToMap');
            const isChecked =
              props.customMappingDataByEdgeId[props.edges[rowIndex].id]?.resultMustBeNonNullToMap ?? false;
            return (
              <Cell>
                <CheckboxInput
                  id={inputId}
                  disabled={BenchlingConfig.WORKFLOWS_ENABLE_CONDITIONAL_MAPPING}
                  checked={isChecked}
                  label="Require field to map"
                  labelStyle={LabelStyle.HIDDEN}
                  onChange={(e) => {
                    props.onChangeCustomMappingData(props.edges[rowIndex].id, {
                      ...(props.customMappingDataByEdgeId[props.edges[rowIndex].id] ?? {}),
                      resultMustBeNonNullToMap: e.target.checked,
                    });
                  }}
                />
              </Cell>
            );
          }}
        />
      )}
    </Table>
  );
}

function InboundMappingsTable(props: TransitionFieldInfoSectionProps): JSX.Element {
  const inboundEdges = props.flowchart.edges.filter(
    (edge) =>
      edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
      edge.targetHandle != null &&
      getSchemaFieldInfoFromHandle(edge.targetHandle).fieldId ===
        getSchemaFieldIdFromRowIdentifier(props.selectedFieldId)
  );

  if (props.transitionSource == null) {
    return <div>No transition source found</div>;
  }

  return (
    <TransitionMappingsTable {...props} transitionSource={props.transitionSource} edges={inboundEdges} />
  );
}

function OutboundMappingsTable(props: TransitionFieldInfoSectionProps): JSX.Element {
  const outboundEdges = props.flowchart.edges.filter(
    (edge) =>
      edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
      edge.sourceHandle != null &&
      getSchemaFieldInfoFromHandle(edge.sourceHandle).fieldId ===
        getSchemaFieldIdFromRowIdentifier(props.selectedFieldId)
  );

  if (props.transitionSource == null) {
    return <div>No transition source found</div>;
  }

  return (
    <TransitionMappingsTable {...props} transitionSource={props.transitionSource} edges={outboundEdges} />
  );
}

export default function TransitionFieldInfoSection(props: TransitionFieldInfoSectionProps): JSX.Element {
  const selectedField = useMemo(() => {
    for (const section of props.node.data.sections) {
      for (const row of section.sectionRows) {
        if (row.id === props.selectedFieldId) {
          return row;
        }
      }
    }
    assert(false, 'Expected to find the selected field in the selected node', {
      selectedFieldId: props.selectedFieldId,
      nodeId: props.node,
    });
  }, [props.selectedFieldId, props.node]);

  // The selected node is the source if there is a control flow edge from the selected node to another node.
  // Otherwise the node is a "target" node.
  const nodeIsSource = props.flowchart.edges.some(
    (edge) =>
      edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW &&
      edge.source === props.node.id &&
      edge.sourceHandle != null
  );
  const nodeIsTarget = props.flowchart.edges.some(
    (edge) =>
      edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW &&
      edge.target === props.node.id &&
      edge.targetHandle != null
  );

  return (
    <>
      <Row bufferRowReset={true}>
        <Col sm={4}>
          <SectionWrapper headerName="Name" elementName="name">
            {selectedField.fieldName}
          </SectionWrapper>
        </Col>
        <Col sm={4}>
          <SectionWrapper headerName="Definition" elementName="definition">
            <FieldDisplay
              field={{
                fieldIcon: selectedField.fieldIcon,
                fieldName: selectedField.fieldTypeLabel,
              }}
            />
          </SectionWrapper>
        </Col>
        <Col sm={4}>
          <SectionWrapper headerName="Task" elementName="task">
            {props.node.data.nodeName}
          </SectionWrapper>
        </Col>
      </Row>

      <Row bufferRowReset={true}>
        <div className={css(styles.mappingTableContainer)}>
          {nodeIsSource && (
            <SectionWrapper headerName="Outbound mappings" elementName="outbound-mappings">
              <div className={css(styles.mappingText)}>Configure how data is mapped from this field.</div>
              <OutboundMappingsTable {...props} />
            </SectionWrapper>
          )}
          {nodeIsTarget && (
            <SectionWrapper headerName="Inbound mappings" elementName="inbound-mappings">
              <div className={css(styles.mappingText)}>Configure how data is mapped to this field.</div>
              <InboundMappingsTable {...props} />
            </SectionWrapper>
          )}
        </div>
      </Row>
    </>
  );
}

const styles = StyleSheet.create({
  mappingText: {
    marginBottom: 16,
  },
  mappingTableContainer: {
    padding: '0 15px',
  },
  fieldDisplay: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
  },
  helpIcon: {
    marginLeft: 8,
  },
  editMappingButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
  },
});
